import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import notificationService from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';

const NotificationsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [unreadCount, setUnreadCount] = useState(0);

  const handleNewNotification = (notification) => {
    if (filter === 'all' || (filter === 'unread' && !notification?.is_read)) {
      setNotifications(prev => [notification, ...prev]);
    }
    setUnreadCount(prev => prev + 1);
  };

  useEffect(() => {
    if (user?.id) {
      loadNotifications();
      loadUnreadCount();

      // Subscribe to real-time notifications
      const channel = notificationService?.subscribeToNotifications(
        user?.id,
        handleNewNotification
      );

      return () => {
        notificationService?.unsubscribeFromNotifications();
      };
    }
  }, [user?.id, filter]);

  const loadNotifications = async () => {
    setIsLoading(true);
    const { data } = await notificationService?.getUserNotifications(user?.id, {
      limit: 100,
      unreadOnly: filter === 'unread'
    });
    if (data) {
      const filteredData =
        filter === 'read' ? data?.filter(n => n?.is_read) : data;
      setNotifications(filteredData);
    }
    setIsLoading(false);
  };

  const loadUnreadCount = async () => {
    const { count } = await notificationService?.getUnreadCount(user?.id);
    setUnreadCount(count || 0);
  };

  const handleNotificationClick = async (notification) => {
    if (!notification?.is_read) {
      await notificationService?.markAsRead(notification?.id);
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev =>
        prev?.map(n => (n?.id === notification?.id ? { ...n, is_read: true } : n))
      );
    }

    if (notification?.action_url) {
      navigate(notification?.action_url);
    }
  };

  const handleMarkAllAsRead = async () => {
    await notificationService?.markAllAsRead(user?.id);
    setUnreadCount(0);
    setNotifications(prev => prev?.map(n => ({ ...n, is_read: true })));
  };

  const handleDeleteNotification = async (notificationId) => {
    await notificationService?.deleteNotification(notificationId);
    setNotifications(prev => prev?.filter(n => n?.id !== notificationId));
    loadUnreadCount();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return { name: 'CheckCircle2', color: 'var(--color-success)' };
      case 'error':
        return { name: 'AlertCircle', color: 'var(--color-destructive)' };
      case 'warning':
        return { name: 'AlertTriangle', color: 'var(--color-warning)' };
      case 'info':
      default:
        return { name: 'Info', color: 'var(--color-primary)' };
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-[1440px] px-4 md:px-6 lg:px-8 py-6 md:py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                Notifications
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Stay updated with your gym activities
              </p>
            </div>
            {unreadCount > 0 && (
              <Button
                onClick={handleMarkAllAsRead}
                variant="outline"
                size="sm"
                iconName="CheckCheck"
              >
                Mark all read
              </Button>
            )}
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 border-b border-border">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                filter === 'all' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              All
              {filter === 'all' && notifications?.length > 0 && (
                <span className="ml-2 text-xs bg-muted px-2 py-0.5 rounded-full">
                  {notifications?.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                filter === 'unread' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Unread
              {unreadCount > 0 && (
                <span className="ml-2 text-xs bg-destructive text-white px-2 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setFilter('read')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                filter === 'read' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Read
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : notifications?.length === 0 ? (
            <div className="bg-card rounded-lg border border-border p-12 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-20 h-20 rounded-full bg-muted/50 flex items-center justify-center">
                  <Icon name="Bell" size={40} className="text-muted-foreground" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                No notifications
              </h3>
              <p className="text-sm text-muted-foreground">
                {filter === 'unread' ? "You're all caught up! No unread notifications."
                  : filter === 'read' ?'No read notifications yet.'
                  : "You don't have any notifications yet."}
              </p>
            </div>
          ) : (
            notifications?.map((notification) => {
              const iconConfig = getNotificationIcon(notification?.type);
              return (
                <div
                  key={notification?.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`bg-card rounded-lg border border-border p-4 hover:shadow-md transition-all cursor-pointer ${
                    !notification?.is_read ? 'bg-primary/5 border-primary/20' : ''
                  }`}
                >
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center">
                        <Icon
                          name={iconConfig?.name}
                          size={24}
                          color={iconConfig?.color}
                        />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="text-base font-semibold text-foreground">
                          {notification?.title}
                        </h4>
                        <button
                          onClick={(e) => {
                            e?.stopPropagation();
                            handleDeleteNotification(notification?.id);
                          }}
                          className="flex-shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                          aria-label="Delete notification"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {notification?.message}
                      </p>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">
                          {formatTimeAgo(notification?.created_at)}
                        </span>
                        {!notification?.is_read && (
                          <span className="flex items-center gap-1 text-xs text-primary">
                            <span className="w-2 h-2 rounded-full bg-primary"></span>
                            Unread
                          </span>
                        )}
                        {notification?.action_url && (
                          <span className="text-xs text-primary flex items-center gap-1">
                            <Icon name="ExternalLink" size={12} />
                            View details
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;